import { convertToFormatedDateString } from '../../../../../services/util/date.service'
import { somenteDigitos } from '../../../../../services/util/documento.service'
import {
  VALORES_FISCAIS_INICIAIS,
  erroValoresFiscais,
  toNum,
} from '../ValoresFiscaisCte/valoresFiscaisCte.util'

// Regras do CTe de Substituição (Ajuste SINIEF 09/07, cláusulas 17 e 17-A), espelho das validações do backend

export const MOTIVO_SUBSTITUICAO_MIN = 15
export const MOTIVO_SUBSTITUICAO_MAX = 255
// Motivo previsto na cláusula 17-A para a troca de tomador
export const MOTIVO_TROCA_TOMADOR = 'tomador informado erroneamente'
export const LIMITE_OBSERVACAO = 2000
// Portal onde o tomador registra o evento de Prestação do Serviço em Desacordo (vale para qualquer UF)
export const URL_PORTAL_DESACORDO =
  'https://dfe-portal.svrs.rs.gov.br/CTE/PrestacaoServicoDesacordo'

const ROTULOS_TOMADOR = { 0: 'Remetente', 3: 'Destinatário', 4: 'Outros' }

export const isCteDeSubcontratacao = cte => cte.document_origin === 'cte'

// CTes com notas anteriores à escolha do tomador não têm o campo gravado e valem Remetente
export function tomadorDoCte(cte) {
  if (isCteDeSubcontratacao(cte)) return '4'
  return cte.tomador || '0'
}

// A troca só existe entre Remetente e Destinatário: não vale para Subcontratação nem para Globalizado
export const aceitaTrocaTomador = cte => !isCteDeSubcontratacao(cte) && !cte.globalizado

export function nomeTomador(cte, tomador = tomadorDoCte(cte)) {
  if (tomador === '4') return cte.cte_origem_emitente
  return tomador === '3' ? cte.client_name : cte.company_name
}

export function descreverTomador(cte, tomador = tomadorDoCte(cte)) {
  const nome = nomeTomador(cte, tomador)
  return nome ? `${ROTULOS_TOMADOR[tomador]} (${nome})` : ROTULOS_TOMADOR[tomador]
}

export function opcoesTomador(cte) {
  return ['0', '3'].map(tomador => ({ value: tomador, label: descreverTomador(cte, tomador) }))
}

// Valores, impostos, CFOP e observações do CTe a substituir, como ponto de partida da correção
export function estadoInicialSubstituicao(cte) {
  const state = { cfop_id: cte.cfop_id, observacoes: cte.observacoes || '' }
  Object.keys(VALORES_FISCAIS_INICIAIS).forEach(campo => {
    const texto = /situacao_tributaria|classificacao_tributaria/.test(campo)
    state[campo] = texto ? cte[campo] || null : toNum(cte[campo])
  })
  return state
}

// Expressão exigida nas observações do CTe substituto, montada pelo backend com o motivo informado
export function expressaoLegal(cte, motivo) {
  const texto = String(motivo || '')
    .trim()
    .replace(/\.+$/, '')
  return `Este documento substitui o CT-e nº ${cte.numero} série ${cte.serie} de ${convertToFormatedDateString(
    cte.data_emissao,
  )} em virtude de ${texto || '(motivo)'}.`
}

export function observacaoCompleta(cte, motivo, observacoes) {
  const livre = String(observacoes || '').trim()
  return livre ? `${expressaoLegal(cte, motivo)} ${livre}` : expressaoLegal(cte, motivo)
}

// Primeira inconsistência do formulário, ou null
export function erroSubstituicao({ cte, state, motivo, cfopSelecionado, telefoneTomador }) {
  if (!cfopSelecionado) return 'Selecione o CFOP'

  const motivoLimpo = String(motivo || '').trim()
  if (
    motivoLimpo.length < MOTIVO_SUBSTITUICAO_MIN ||
    motivoLimpo.length > MOTIVO_SUBSTITUICAO_MAX
  ) {
    return `Informe o Motivo da substituição (entre ${MOTIVO_SUBSTITUICAO_MIN} e ${MOTIVO_SUBSTITUICAO_MAX} caracteres)`
  }

  if (isCteDeSubcontratacao(cte)) {
    const telefone = somenteDigitos(telefoneTomador)
    if (telefone.length < 6 || telefone.length > 14) {
      return 'Informe o Telefone do Tomador (emitente do CTe de origem) com 6 a 14 dígitos'
    }
  }

  const erroFiscal = erroValoresFiscais(state)
  if (erroFiscal) return erroFiscal

  if (observacaoCompleta(cte, motivoLimpo, state.observacoes).length > LIMITE_OBSERVACAO) {
    return `O Motivo e as Observações, somados à expressão legal da substituição, ultrapassam ${LIMITE_OBSERVACAO} caracteres`
  }
  return null
}

// O que muda do CTe original para o substituto, para a confirmação: [{ rotulo, antes, depois, moeda }]
export function resumoAlteracoes(cte, state, tomador) {
  const alteracoes = []
  if (tomador !== tomadorDoCte(cte)) {
    alteracoes.push({
      rotulo: 'Tomador',
      antes: descreverTomador(cte),
      depois: descreverTomador(cte, tomador),
    })
  }
  const valores = [
    { rotulo: 'Valor Bruto Frete', campo: 'valor_total' },
    { rotulo: 'Valor Líquido Frete', campo: 'valor_receber' },
    { rotulo: 'Valor ICMS', campo: 'icms_valor' },
  ]
  valores.forEach(({ rotulo, campo }) => {
    const antes = toNum(cte[campo])
    const depois = toNum(state[campo])
    if (antes !== depois) alteracoes.push({ rotulo, antes, depois, moeda: true })
  })
  return alteracoes
}

// Para valor cobrado a menor o instrumento é o CTe Complementar (cláusula 17, § 3º)
export const valorMaiorQueOriginal = (cte, state) =>
  toNum(state.valor_total) > toNum(cte.valor_total)

// Datas de GET /cte/substituicao/dados chegam no horário local (AAAA-MM-DDTHH:mm:ss)
export const prazoVencido = limite => Boolean(limite) && new Date(limite).getTime() < Date.now()
